import { ChannelAvailabilityInterface, ConversationChannel } from '@vendasta/conversation';
import { AlertOptions } from './conversation-channel.abstract';
import { ConversationChannelCompat, ConversationDetail } from '../interface/conversation.interface';

// Whatsapp errors: https://developers.facebook.com/docs/whatsapp/cloud-api/support/error-codes/
export const WHATSAPP_ERROR_TRANSLATION_MAP: Record<string, string> = {
  '100': 'INBOX.ERROR.MESSAGE_ERROR.INVALID_PARAMETER',
  '551': 'INBOX.ERROR.MESSAGE_ERROR.RECIPIENT_UNAVAILABLE',
  '130472': 'INBOX.ERROR.MESSAGE_ERROR.MARKETING_LIMIT',
  '131026': 'INBOX.ERROR.MESSAGE_ERROR.UNABLE_TO_DELIVER',
  '131037': 'INBOX.ERROR.MESSAGE_ERROR.DISPLAY_NAME_NOT_APPROVED',
  '131042': 'INBOX.ERROR.MESSAGE_ERROR.PAYMENT_ISSUE',
  '131045': 'INBOX.ERROR.MESSAGE_ERROR.PHONE_NUMBER_REGISTRATION',
  '131047': 'INBOX.ERROR.MESSAGE_ERROR.MORE_THAN_24_HOURS',
  '131049': 'INBOX.ERROR.MESSAGE_ERROR.MARKETING_LIMIT',
  '132015': 'INBOX.ERROR.MESSAGE_ERROR.TEMPLATE_PAUSED',
  '138000': 'INBOX.ERROR.MESSAGE_ERROR.CALLING_NOT_ENABLED',
};

export const WHATSAPP_INBOX_ERROR_TRANSLATION_MAP: Record<string, string> = {
  displayNameNotApproved: 'INBOX.ERROR.MESSAGE_ERROR.DISPLAY_NAME_NOT_APPROVED',
  callingNotEnabled: 'INBOX.ERROR.MESSAGE_ERROR.CALLING_NOT_ENABLED',
  recipientUnavailable: 'INBOX.ERROR.MESSAGE_ERROR.RECIPIENT_UNAVAILABLE',
  invalidParameter: 'INBOX.ERROR.MESSAGE_ERROR.INVALID_PARAMETER',
  reEngagementRequired: 'INBOX.ERROR.MESSAGE_ERROR.MORE_THAN_24_HOURS',
  undeliverable: 'INBOX.ERROR.MESSAGE_ERROR.UNABLE_TO_DELIVER',
  paymentIssue: 'INBOX.ERROR.MESSAGE_ERROR.PAYMENT_ISSUE',
  senderNotRegistered: 'INBOX.ERROR.MESSAGE_ERROR.PHONE_NUMBER_REGISTRATION',
  marketingLimit: 'INBOX.ERROR.MESSAGE_ERROR.MARKETING_LIMIT',
  templatePaused: 'INBOX.ERROR.MESSAGE_ERROR.TEMPLATE_PAUSED',
  invalidRecipient: 'INBOX.ERROR.MESSAGE_ERROR.INVALID_RECIPIENT',
  unsupportedContent: 'INBOX.ERROR.MESSAGE_ERROR.UNSUPPORTED_CONTENT',
  notConnected: 'INBOX.ERROR.MESSAGE_ERROR.NOT_CONNECTED',
};

export const INBOX_ERROR_CODE_TRANSLATION_MAP: Record<string, string> = {
  unsubscribed: 'INBOX.SEND_MESSAGE.SMS.ERRORS.UNSUBSCRIBED',
  accountSuspended: 'INBOX.SEND_MESSAGE.SMS.ERRORS.ACCOUNT_SUSPENDED',
  messageBlocked: 'INBOX.SEND_MESSAGE.SMS.ERRORS.MESSAGE_BLOCKED',
  unknownDestination: 'INBOX.SEND_MESSAGE.SMS.ERRORS.UNKNOWN_DESTINATION',
  mediaSizeExceeded: 'INBOX.SEND_MESSAGE.SMS.ERRORS.MEDIA_SIZE_EXCEEDED',
  invalidContentType: 'INBOX.SEND_MESSAGE.SMS.ERRORS.INVALID_CONTENT_TYPE',
  toNumberIsShortCode: 'INBOX.SEND_MESSAGE.SMS.ERRORS.TO_NUMBER_IS_SHORT_CODE',
  sameToFromNumbers: 'INBOX.SEND_MESSAGE.SMS.ERRORS.SAME_TO_FROM_NUMBERS',
  premiumNumberSendingNotAllowed: 'INBOX.SEND_MESSAGE.SMS.ERRORS.PREMIUM_NUMBER_SENDING_NOT_ALLOWED',
  messageBodyTooLong: 'INBOX.SEND_MESSAGE.SMS.ERRORS.MESSAGE_BODY_TOO_LONG',
  messageFiltered: 'INBOX.SEND_MESSAGE.SMS.ERRORS.MESSAGE_FILTERED',
  toNumberRegionNotEnabled: 'INBOX.SEND_MESSAGE.SMS.ERRORS.REGION_NOT_ENABLED',
  invalidToNumber: 'INBOX.SEND_MESSAGE.SMS.ERRORS.INVALID_TO_NUMBER',
  toNumberIsLandline: 'INBOX.SEND_MESSAGE.SMS.ERRORS.TO_NUMBER_IS_LANDLINE',
  temporaryIssue: 'INBOX.SEND_MESSAGE.SMS.ERRORS.TEMPORARY_ISSUE',
  systemError: 'INBOX.SEND_MESSAGE.SMS.ERRORS.SYSTEM_ERROR',
  a2pIssue: 'INBOX.SEND_MESSAGE.SMS.ERRORS.A2P_ISSUE',
};

export function getErrorMessage(channel: ConversationChannel, code: string): string {
  if (channel === ConversationChannel.CONVERSATION_CHANNEL_WHATSAPP) {
    return WHATSAPP_ERROR_TRANSLATION_MAP[code] || '';
  }
  return '';
}

export function getInboxSMSErrorTranslationKey(errorCode: string): string {
  return INBOX_ERROR_CODE_TRANSLATION_MAP[errorCode] || 'INBOX.SEND_MESSAGE.SMS.ERRORS.SYSTEM_ERROR';
}

export function getSendErrorTranslationKey(channel: ConversationChannelCompat, errorCode: string): string {
  if (channel === ConversationChannel.CONVERSATION_CHANNEL_WHATSAPP || channel === 'CONVERSATION_CHANNEL_WHATSAPP') {
    return WHATSAPP_INBOX_ERROR_TRANSLATION_MAP[errorCode] || 'INBOX.SEND_MESSAGE.SMS.ERRORS.SYSTEM_ERROR';
  }
  return getInboxSMSErrorTranslationKey(errorCode);
}

export function buildAlertOptionsFromAvailability(
  channel: ConversationChannel,
  availability: ChannelAvailabilityInterface[],
): AlertOptions | null {
  const channelAvailability = availability.find((channelAvailability) => channelAvailability.channel === channel);

  if (channelAvailability?.statuses && channelAvailability.statuses.length > 0) {
    return {
      title: channelAvailability.statuses?.[0]?.i18nKey,
    } as AlertOptions;
  }

  return null;
}

export const MAIN_CONVERSATION_ID_KEY = 'main_conversation_id_key';

export function getMainConversationId(conversationDetail: ConversationDetail): string {
  const kv = conversationDetail?.conversation?.metadata?.find((meta) => meta.key === MAIN_CONVERSATION_ID_KEY);
  return kv?.value ?? '';
}

// Conversation ids travel as `CONVERSATION-<id>` but routes carry the bare id. Anchored so an id
// that merely contains the token is left alone.
export function toConversationRouteId(conversationId: string): string {
  return conversationId.replace(/^CONVERSATION-/, '');
}
